import { groupSkills, selectValidCertifications } from '../../utils/documentSelectors';
import { getSkillCategoryLabel } from '../../../utils/skillCategories';

const compact = (pages) => pages.filter(
  (page) => page.items?.length || page.groups?.length || page.kind === 'summary' || page.kind === 'cover'
);

const orderedByIds = (items = [], ids = []) => {
  if (!ids?.length) return items;
  const byId = new Map(items.map((item) => [String(item.id), item]));
  const ordered = ids.map((id) => byId.get(String(id))).filter(Boolean);
  const selectedIds = new Set(ordered.map((item) => String(item.id)));
  return [...ordered, ...items.filter((item) => !selectedIds.has(String(item.id)))];
};

const splitExperiences = (items = [], order = []) => {
  const ordered = orderedByIds(items, order);
  const featured = ordered.filter((item) => item.isFeatured === true);
  const featuredIds = new Set(featured.map((item) => String(item.id)));
  const remaining = ordered.filter((item) => !featuredIds.has(String(item.id)));
  const primary = [...featured, ...remaining].slice(0, 3);
  const primaryIds = new Set(primary.map((item) => String(item.id)));
  return {
    primary,
    secondary: ordered.filter((item) => !primaryIds.has(String(item.id))),
  };
};

const chunks = (items = [], size) => Array.from(
  { length: Math.ceil(items.length / size) },
  (_, index) => items.slice(index * size, index * size + size)
);

const skillGroups = (skills = []) => Object.entries(groupSkills(skills))
  .slice(0, 8)
  .map(([category, items]) => ({ category: getSkillCategoryLabel(category), items: items.slice(0, 8) }));

export const resolvePortfolioLayout = (data = {}, options = {}) => {
  const pages = [];
  const enabled = (key) => options.sections?.[key] !== false;

  if (options.showCover !== false) pages.push({ kind: 'cover' });
  pages.push({ kind: 'summary' });

  if (enabled('skills')) pages.push({ kind: 'skills', groups: skillGroups(data.skills || []) });

  if (enabled('experiences')) {
    const { primary, secondary } = splitExperiences(data.experiences || [], options.experienceOrder);
    chunks(primary, 3).forEach((items) => pages.push({ kind: 'primary-experiences', items }));
    chunks(secondary, 3).forEach((items) => pages.push({ kind: 'secondary-experiences', items }));
  }

  if (enabled('references')) {
    const allReferences = data.references || [];
    const selectedReferences = options.referenceIds?.length
      ? options.referenceIds.map((id) => allReferences.find((item) => String(item.id) === String(id))).filter(Boolean)
      : allReferences;
    const references = selectedReferences
      .filter((item) => item.isAuthorized !== false)
      .slice(0, 8);
    chunks(references, 2).forEach((items) => pages.push({ kind: 'references', items }));
  }

  const education = enabled('education') ? (data.educations || []).slice(0, 7) : [];
  const certifications = enabled('certifications')
    ? selectValidCertifications(data.certifications || [], 30).slice(0, 10)
    : [];
  const trainings = (data.trainings || []).filter((item) => item.showInSkillsPortfolio !== false).slice(0, 8);
  pages.push({ kind: 'credentials', items: education, trainings, certifications });

  const order = Array.isArray(options.sectionOrder) ? options.sectionOrder : [];
  const sectionKey = (page) => page.kind === 'summary' ? 'presentation'
    : page.kind === 'skills' ? 'skills'
      : page.kind.includes('experiences') ? 'experiences'
        : page.kind === 'references' ? 'references'
          : page.kind === 'credentials' ? ['trainings', 'education', 'certifications'].find((key) => order.includes(key)) || 'education'
            : 'cover';
  const resolved = compact(pages).map((page, index) => ({ ...page, originalIndex: index })).sort((left, right) => {
    if (left.kind === 'cover') return -1;
    if (right.kind === 'cover') return 1;
    const leftIndex = order.indexOf(sectionKey(left)); const rightIndex = order.indexOf(sectionKey(right));
    return (leftIndex < 0 ? Number.MAX_SAFE_INTEGER : leftIndex) - (rightIndex < 0 ? Number.MAX_SAFE_INTEGER : rightIndex) || left.originalIndex - right.originalIndex;
  });
  return resolved.map(({ originalIndex, ...page }, index) => ({ ...page, pageNumber: index + 1, totalPages: resolved.length }));
};

export const portfolioPageTitle = (kind) => ({
  summary: 'Synthèse du profil',
  skills: 'Compétences détaillées',
  'primary-experiences': 'Expériences et missions majeures',
  'secondary-experiences': 'Expériences et missions · sélection complémentaire',
  references: 'Références projets',
  credentials: 'Formation et habilitations',
}[kind] || 'Dossier de compétences');
